import type { MarketVariants, SiteVariants } from '@/lib/i18n'

export type Slide3CharityCopy = {
  title: string
  emailSubject: string
  senderInitials: string
  senderName: string
  senderEmail: string
  senderToLine: string
  /** Salutation used when the learner's first name is known. `{name}` is
   *  replaced at render time; the string must end with the separator that
   *  precedes `bodyOpenLower` (e.g. "Dear {name}, "). */
  greeting: string
  /** Body opening. Rendered as `{greeting}{bodyOpenLower}` when the learner's
   *  first name is available, else `bodyOpenUpper` on its own. In English the
   *  two differ only by the capital first letter; in Arabic they are identical. */
  bodyOpenLower: string
  bodyOpenUpper: string
  bodyMatch: string
  bodyCta: string
  /** Hover tooltip showing the real (malicious) URL. */
  realUrl: string

  trustItem1: string
  trustItem2: string
  pressureEmpathy: string
  pressureGuilt: string
  pressureUrgency: string
  actionItem: string

  trustLabel: string
  emotionLabel: string
  actionLabel: string
  revealButton: string
}

export const copy: MarketVariants<Slide3CharityCopy> = {
  US: {
    title: 'Fake charity appeals',
    emailSubject: 'Urgent: Children of Fallen Heroes Need You',
    senderInitials: 'FR',
    senderName: 'First Responders Fund',
    senderEmail: 'donate@responders-fund.org',
    senderToLine: 'to me',
    greeting: 'Dear {name}, ',
    bodyOpenLower:
      'families of fallen and injured firefighters need immediate support. Even a small gift can help.',
    bodyOpenUpper:
      'Families of fallen and injured firefighters need immediate support. Even a small gift can help.',
    bodyMatch:
      'A donor is matching every gift, but only until midnight tonight. ',
    bodyCta: "Please give now before it's too late.",
    realUrl: 'responders-childrens-fund.org/help',

    trustItem1: 'Official-sounding name',
    trustItem2: 'Mimics real charity branding',
    pressureEmpathy: 'Empathy: "children of fallen heroes"',
    pressureGuilt: 'Guilt: "even a small gift can help"',
    pressureUrgency: 'Urgency: fake deadline, "before it\'s too late"',
    actionItem: 'Click the donate link',

    trustLabel: 'Gain trust',
    emotionLabel: 'Trigger emotion',
    actionLabel: 'Push action',
    revealButton: 'Reveal the pattern',
  },
  // Qatar face: a US first-responder charity means little in Doha, so the
  // appeal becomes the locally recognisable shape (a Ramadan / Eid zakat
  // appeal for orphans, the kind the Regulatory Authority for Charitable
  // Activities warns about every year). The organisation and both domains are
  // fictional; the sender domain still differs from the real link so the
  // domain-mismatch teaching point survives. Guilt and urgency phrases are
  // unchanged so only the empathy callout is re-pointed.
  QA: {
    emailSubject: 'Urgent: Orphans Need Your Zakat Before Eid',
    senderInitials: 'GR',
    senderName: 'Gulf Relief Foundation',
    senderEmail: 'donate@gulf-relief-foundation.org',
    bodyOpenLower:
      'orphaned children in war-torn regions need immediate support. Even a small gift can help.',
    bodyOpenUpper:
      'Orphaned children in war-torn regions need immediate support. Even a small gift can help.',
    realUrl: 'gulf-relief-donations.org/give',
    pressureEmpathy: 'Empathy: "orphans", "orphaned children"',
  },
  ar: {
    title: 'نداءات خيرية مزيفة',
    emailSubject: 'عاجل: الأيتام بحاجة إلى زكاتك قبل العيد',
    senderInitials: 'م',
    senderName: 'مؤسسة الخليج للإغاثة',
    senderEmail: 'donate@gulf-relief-foundation.org',
    senderToLine: 'إليّ',
    greeting: 'عزيزنا {name}، ',
    bodyOpenLower:
      'الأطفال الأيتام في مناطق الحروب بحاجة إلى دعم فوري. حتى التبرع البسيط يمكن أن يساعد.',
    bodyOpenUpper:
      'الأطفال الأيتام في مناطق الحروب بحاجة إلى دعم فوري. حتى التبرع البسيط يمكن أن يساعد.',
    bodyMatch:
      'أحد المتبرعين يضاعف كل تبرع، لكن حتى منتصف هذه الليلة فقط. ',
    bodyCta: 'تبرّع الآن قبل فوات الأوان.',
    realUrl: 'gulf-relief-donations.org/give',

    trustItem1: 'اسم يبدو رسمياً',
    trustItem2: 'يقلّد هوية الجمعيات الخيرية الحقيقية',
    pressureEmpathy: 'التعاطف: "الأيتام"، "الأطفال الأيتام"',
    pressureGuilt: 'الشعور بالذنب: "حتى التبرع البسيط يمكن أن يساعد"',
    pressureUrgency: 'الاستعجال: موعد نهائي وهمي، "قبل فوات الأوان"',
    actionItem: 'النقر على رابط التبرع',

    trustLabel: 'كسب الثقة',
    emotionLabel: 'إثارة المشاعر',
    actionLabel: 'الدفع إلى التصرف',
    revealButton: 'اكشف النمط',
  },
}

// DCUC (military) tenant: a fake fallen/wounded service-member charity in place
// of the first-responder one. Name and URL are fictional-but-plausible (they
// evoke, rather than copy, real veteran charities, same approach as the US
// base). The sender domain (fallenheroes-fund.org) deliberately differs from
// the real link (fallen-heroes-relief.org) to preserve the domain-mismatch
// teaching point. bodyMatch / bodyCta / guilt / urgency callouts are unchanged,
// so only the empathy callout is re-pointed at the new appeal.
const militaryCharity: Partial<Slide3CharityCopy> = {
  emailSubject: 'Urgent: Families of the Fallen Need You',
  senderInitials: 'FH',
  senderName: 'Fallen Heroes Family Fund',
  senderEmail: 'donate@fallenheroes-fund.org',
  bodyOpenLower:
    'families of fallen and wounded service members need immediate support. Even a small gift can help.',
  bodyOpenUpper:
    'Families of fallen and wounded service members need immediate support. Even a small gift can help.',
  realUrl: 'fallen-heroes-relief.org/help',
  pressureEmpathy: 'Empathy: "families of the fallen"',
}

export const siteCopy: SiteVariants<Slide3CharityCopy> = {
  dcuc: militaryCharity,
  amba: militaryCharity,
}
